import React, { useState } from 'react';
import AttachmentUpload from './AttachmentUpload';

const UniversalOrlandoPackageForm = ({ onSave, onCancel, initialData = {} }) => {
  const [formData, setFormData] = useState({
    title: 'Universal Orlando Resort Package',
    resort_name: '',
    room_type: '',
    view: '',
    check_in_date: '',
    check_out_date: '',
    ticket_type: '2-Day 3-Park Ticket (includes Epic Universe)',
    express_pass: 'None',
    early_park_admission: 'Included',
    dining_plan: 'No Dining Plan',
    citywalk_venues: '',
    ground_transportation: '',
    travel_protection: 'Not Included',
    travel_protection_price: 0,
    total_price: 0,
    total_price_due_by: '',
    deposit_due: 0,
    deposit_due_by: '',
    special_requests: '',
    children: [],
    isOption: false,
    optionDescription: '',
    photos: [],
    documents: [],
    ...initialData
  });

  const [errors, setErrors] = useState({});

  // Universal Orlando Resort Hotels
  const resortOptions = [
    // Epic Universe Hotel
    "Universal Helios Grand Hotel at Epic Universe",
    // Premier Hotels (Express Pass Included)
    "Hard Rock Hotel",
    "Loews Portofino Bay Hotel", 
    "Loews Royal Pacific Resort",
    // Preferred Hotels
    "Loews Sapphire Falls Resort",
    "Universal's Stella Nova Resort",
    "Universal's Terra Luna Resort",
    // Prime Value Hotels
    "Universal's Aventura Hotel",
    "Universal's Cabana Bay Beach Resort",
    "Universal's Endless Summer Resort - Surfside Inn and Suites",
    "Universal's Endless Summer Resort - Dockside Inn and Suites",
    // Partner Hotels
    "DoubleTree by Hilton Orlando at SeaWorld",
    "Hilton Orlando",
    "Holiday Inn Resort Orlando Suites - Waterpark",
    "Hyatt House Across from Universal Orlando Resort",
    "Hyatt Place Across from Universal Orlando Resort"
  ];

  const ticketTypes = [
    '1-Day Single Park Ticket',
    '2-Day Single Park Ticket', 
    '3-Day Single Park Ticket',
    '4-Day Single Park Ticket',
    '5-Day Single Park Ticket',
    '6-Day Single Park Ticket',
    '7-Day Single Park Ticket',
    '1-Day Park-to-Park Ticket (2 Parks)',
    '2-Day Park-to-Park Ticket (2 Parks)',
    '3-Day Park-to-Park Ticket (2 Parks)', 
    '4-Day Park-to-Park Ticket (2 Parks)',
    '5-Day Park-to-Park Ticket (2 Parks)',
    '6-Day Park-to-Park Ticket (2 Parks)',
    '7-Day Park-to-Park Ticket (2 Parks)',
    '1-Day 3-Park Ticket (includes Epic Universe)',
    '2-Day 3-Park Ticket (includes Epic Universe)',
    '3-Day 3-Park Ticket (includes Epic Universe)',
    '4-Day 3-Park Ticket (includes Epic Universe)',
    '5-Day 3-Park Ticket (includes Epic Universe)',
    '6-Day 3-Park Ticket (includes Epic Universe)',
    '7-Day 3-Park Ticket (includes Epic Universe)'
  ];

  const expressPassOptions = [
    'None',
    'Universal Express Pass',
    'Universal Express Pass Unlimited'
  ];

  const diningPlanOptions = [
    'No Dining Plan',
    'Universal Dining Plan',
    'Universal Quick Service Dining Plan'
  ];

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleChildrenChange = (count) => {
    const children = Array.from({ length: count }, (_, i) => 
      formData.children[i] || { age: 5 }
    );
    setFormData(prev => ({ ...prev, children }));
  };

  const handleChildAgeChange = (index, age) => {
    const updatedChildren = [...formData.children];
    updatedChildren[index] = { age: parseInt(age) };
    setFormData(prev => ({ ...prev, children: updatedChildren }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.check_in_date) newErrors.check_in_date = 'Check-in date is required';
    if (!formData.check_out_date) newErrors.check_out_date = 'Check-out date is required';
    if (!formData.resort_name) newErrors.resort_name = 'Resort selection is required';
    if (!formData.total_price) newErrors.total_price = 'Total price is required';
    if (!formData.deposit_due) newErrors.deposit_due = 'Deposit amount is required';

    // Date validation
    if (formData.check_in_date && formData.check_out_date) {
      const checkIn = new Date(formData.check_in_date);
      const checkOut = new Date(formData.check_out_date);
      if (checkOut <= checkIn) {
        newErrors.check_out_date = 'Check-out date must be after check-in date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Universal Orlando form submitted with data:', formData);
    
    if (validateForm()) {
      const componentData = {
        type: 'universal-orlando-package',
        title: formData.title,
        price: formData.total_price,
        isOption: formData.isOption,
        optionDescription: formData.optionDescription,
        paymentTiming: formData.paymentTiming,
        optionalDepositAmount: formData.optionalDepositAmount,
        photos: formData.photos,
        documents: formData.documents,
        data: {
          resort_name: formData.resort_name,
          room_type: formData.room_type,
          view: formData.view,
          check_in_date: formData.check_in_date,
          check_out_date: formData.check_out_date,
          ticket_type: formData.ticket_type,
          express_pass: formData.express_pass,
          early_park_admission: formData.early_park_admission,
          dining_plan: formData.dining_plan,
          citywalk_venues: formData.citywalk_venues,
          ground_transportation: formData.ground_transportation,
          travel_protection: formData.travel_protection,
          travel_protection_price: formData.travel_protection_price,
          total_price: formData.total_price,
          total_price_due_by: formData.total_price_due_by,
          deposit_due: formData.deposit_due,
          deposit_due_by: formData.deposit_due_by,
          special_requests: formData.special_requests,
          children: formData.children
        }
      };
      console.log('Calling onSave with:', componentData);
      onSave(componentData);
    } else {
      console.log('Form validation failed with errors:', errors);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="component-form universal-orlando-package-form">
      <div className="form-section">
        <h4>Package Details</h4>
        <div className="form-group">
          <label>Package Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="e.g., Universal Orlando Resort Package"
            className="form-control"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Resort Accommodation</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Check-in Date *</label>
            <input
              type="date"
              name="check_in_date"
              value={formData.check_in_date}
              onChange={handleInputChange}
              className={`form-control ${errors.check_in_date ? 'error' : ''}`}
            />
            {errors.check_in_date && <span className="error-text">{errors.check_in_date}</span>}
          </div>
          <div className="form-group">
            <label>Check-out Date *</label>
            <input
              type="date"
              name="check_out_date"
              value={formData.check_out_date}
              onChange={handleInputChange}
              className={`form-control ${errors.check_out_date ? 'error' : ''}`}
            />
            {errors.check_out_date && <span className="error-text">{errors.check_out_date}</span>}
          </div>
        </div>
        
        <div className="form-group">
          <label>Resort/Hotel Name *</label>
          <select
            name="resort_name"
            value={formData.resort_name}
            onChange={handleInputChange}
            className={`form-control ${errors.resort_name ? 'error' : ''}`}
          >
            <option value="">Select a Resort/Hotel</option>
            <optgroup label="Epic Universe Hotel">
              <option value="Universal Helios Grand Hotel at Epic Universe">Universal Helios Grand Hotel at Epic Universe</option>
            </optgroup>
            <optgroup label="Premier Hotels (Express Pass Included)">
              <option value="Hard Rock Hotel">Hard Rock Hotel</option>
              <option value="Loews Portofino Bay Hotel">Loews Portofino Bay Hotel</option>
              <option value="Loews Royal Pacific Resort">Loews Royal Pacific Resort</option>
            </optgroup>
            <optgroup label="Preferred Hotels">
              <option value="Loews Sapphire Falls Resort">Loews Sapphire Falls Resort</option>
              <option value="Universal's Stella Nova Resort">Universal's Stella Nova Resort</option>
              <option value="Universal's Terra Luna Resort">Universal's Terra Luna Resort</option>
            </optgroup>
            <optgroup label="Prime Value Hotels">
              <option value="Universal's Aventura Hotel">Universal's Aventura Hotel</option>
              <option value="Universal's Cabana Bay Beach Resort">Universal's Cabana Bay Beach Resort</option>
              <option value="Universal's Endless Summer Resort - Surfside Inn and Suites">Universal's Endless Summer Resort - Surfside Inn and Suites</option>
              <option value="Universal's Endless Summer Resort - Dockside Inn and Suites">Universal's Endless Summer Resort - Dockside Inn and Suites</option>
            </optgroup>
            <optgroup label="Partner Hotels">
              <option value="DoubleTree by Hilton Orlando at SeaWorld">DoubleTree by Hilton Orlando at SeaWorld</option>
              <option value="Hilton Orlando">Hilton Orlando</option>
              <option value="Holiday Inn Resort Orlando Suites - Waterpark">Holiday Inn Resort Orlando Suites - Waterpark</option>
              <option value="Hyatt House Across from Universal Orlando Resort">Hyatt House Across from Universal Orlando Resort</option>
              <option value="Hyatt Place Across from Universal Orlando Resort">Hyatt Place Across from Universal Orlando Resort</option>
            </optgroup>
          </select>
          {errors.resort_name && <span className="error-text">{errors.resort_name}</span>}
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Room Type</label>
            <input
              type="text"
              name="room_type"
              value={formData.room_type}
              onChange={handleInputChange}
              placeholder="e.g., Standard Room, Suite"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>View</label>
            <input
              type="text"
              name="view"
              value={formData.view}
              onChange={handleInputChange}
              placeholder="e.g., Pool View, City View"
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Theme Park Tickets</h4>
        <div className="form-group">
          <label>Ticket Type</label>
          <select
            name="ticket_type"
            value={formData.ticket_type}
            onChange={handleInputChange}
            className="form-control"
          >
            {ticketTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Express Pass</label>
            <select
              name="express_pass"
              value={formData.express_pass}
              onChange={handleInputChange}
              className="form-control"
            >
              {expressPassOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Early Park Admission</label>
            <select
              name="early_park_admission"
              value={formData.early_park_admission}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="Included">Included</option>
              <option value="Not Included">Not Included</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Dining & Entertainment</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Dining Plan</label>
            <select
              name="dining_plan"
              value={formData.dining_plan}
              onChange={handleInputChange}
              className="form-control"
            >
              {diningPlanOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>CityWalk Venues</label>
            <input
              type="text"
              name="citywalk_venues"
              value={formData.citywalk_venues}
              onChange={handleInputChange}
              placeholder="e.g., Hard Rock Cafe, Jimmy Buffett's Margaritaville"
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Add-on Services</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Ground Transportation</label>
            <input
              type="text"
              name="ground_transportation"
              value={formData.ground_transportation}
              onChange={handleInputChange}
              placeholder="Transportation details"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label>Travel Protection</label>
            <select
              name="travel_protection"
              value={formData.travel_protection}
              onChange={handleInputChange}
              className="form-control"
            >
              <option value="Not Included">Not Included</option>
              <option value="Included">Included</option>
            </select>
          </div>
        </div>
        
        {formData.travel_protection === 'Included' && (
          <div className="form-group">
            <label>Travel Protection Price ($)</label>
            <input
              type="number"
              name="travel_protection_price"
              value={formData.travel_protection_price}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className="form-control"
            />
          </div>
        )}
      </div>

      <div className="form-section">
        <h4>Pricing & Payment</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Total Price ($) *</label>
            <input
              type="number"
              name="total_price"
              value={formData.total_price}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`form-control ${errors.total_price ? 'error' : ''}`}
            />
            {errors.total_price && <span className="error-text">{errors.total_price}</span>}
          </div>
          <div className="form-group">
            <label>Total Price Due By</label>
            <input
              type="date"
              name="total_price_due_by"
              value={formData.total_price_due_by}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label>Deposit Due ($) *</label>
            <input
              type="number"
              name="deposit_due"
              value={formData.deposit_due}
              onChange={handleInputChange}
              step="0.01"
              min="0"
              className={`form-control ${errors.deposit_due ? 'error' : ''}`}
            />
            {errors.deposit_due && <span className="error-text">{errors.deposit_due}</span>}
          </div>
          <div className="form-group">
            <label>Deposit Due By</label>
            <input
              type="date"
              name="deposit_due_by"
              value={formData.deposit_due_by}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Additional Information</h4>
        <div className="form-group">
          <label>Special Requests</label>
          <textarea
            name="special_requests"
            value={formData.special_requests}
            onChange={handleInputChange}
            rows="3"
            className="form-control"
            placeholder="Any special requests or preferences for this Universal Orlando package"
          />
        </div>
      </div>

      <div className="form-section">
        <h4>Client Options</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isOption}
                onChange={(e) => setFormData({...formData, isOption: e.target.checked})}
              />
              Mark as Optional for Client
            </label>
            <small className="form-help">
              When checked, clients can choose whether to include this component in their booking
            </small>
          </div>
        </div>

        {formData.isOption && (
          <>
            <div className="form-group">
              <label>Option Description for Client</label>
              <textarea
                value={formData.optionDescription}
                onChange={(e) => setFormData({...formData, optionDescription: e.target.value})}
                placeholder="e.g., Optional Universal Orlando package upgrade or add-on"
                rows="2"
              />
              <small className="form-help">
                This description helps clients understand what this option includes
              </small>
            </div>

            <div className="form-group">
              <label>Payment Timing for Optional Component</label>
              <select
                value={formData.paymentTiming}
                onChange={(e) => setFormData({...formData, paymentTiming: e.target.value})}
                className="form-control"
              >
                <option value="immediate">Pay Full Price When Selected (e.g., cruise excursions)</option>
                <option value="with_deposit">Pay Deposit When Selected</option>
                <option value="with_final">Pay with Final Payment (nothing due initially)</option>
                <option value="bundled">Already Included in Package (no additional payment)</option>
              </select>
              <small className="form-help">
                Choose when payment is due if client selects this option
              </small>
            </div>

            {formData.paymentTiming === 'with_deposit' && (
              <div className="form-group">
                <label>Optional Component Deposit Amount ($)</label>
                <input
                  type="number"
                  value={formData.optionalDepositAmount}
                  onChange={(e) => setFormData({...formData, optionalDepositAmount: e.target.value})}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="form-control"
                />
                <small className="form-help">
                  Amount due when client selects this option (leave blank for full price)
                </small>
              </div>
            )}
          </>
        )}
      </div>

      <AttachmentUpload
        photos={formData.photos || []}
        documents={formData.documents || []}
        onPhotosChange={(photos) => setFormData({...formData, photos})}
        onDocumentsChange={(documents) => setFormData({...formData, documents})}
        maxPhotos={5}
        maxDocuments={3}
        showPhotos={true}
        showDocuments={true}
      />

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Add to Quote
        </button>
      </div>
    </form>
  );
};

export default UniversalOrlandoPackageForm;
